import React, { useEffect, useRef, useState } from "react";
import { Card, Col, Container, Row } from "react-bootstrap";
import NewsTable from "../../components/dataTables/news/newsTable";
import ParallelButtonSelection from "../../components/selections/periodSelection/parallelButtonSelection";
import { KapApi } from "../../services/news/api";
import { Configuration } from "../../services/stockFiltering/configuration";
import Spinner from "../../components/spinner";
import LiveTickerBar from "../../components/live-ticker-bar/liveTickerBar";
import moment from "moment";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { APIControl } from "../../helpers/APIControl";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSpinner } from '@fortawesome/pro-solid-svg-icons';
import './news.scss';

// Adjust limit for optimal performance - a more reasonable batch size
const ITEMS_PER_PAGE = 30;

const News = ({
  entityIds,
  constutientEntityId,
  showLiveTicker,
  mainNews = false,
  hasEntityId = false,
  visibleSearch = true,
  beginOfDate = 1, // Default 1 year history
  hideOtherCategory = true // When true, hides the "Diğer (Other)" tab
}) => {
  const { t } = useTranslation();
  const token = useSelector((s) => s.auth?.token);

  const [newsTableData, setNewsTableData] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [listLoading, setListLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);

  // Refs to track pagination state
  const offsetRef = useRef(0);
  const loadingRef = useRef(false);
  const requestIdRef = useRef(0); // For preventing race conditions

  const columns = [
    { id: 1, field: "source", header: "" },
    { id: 2, field: "uniqueCode", header: "" },
    { id: 3, field: "detail", header: "" },
    { id: 4, field: "attachment", header: "" }
  ];

  const api = new KapApi(
    new Configuration({ basePath: process.env.REACT_APP_NEWS_API_BASE_PATH }),
    undefined,
    APIControl(process.env.REACT_APP_NEWS_API_BASE_PATH)
  );

  /* ---------------- Kategori listesi ---------------- */
  const allNewsCategories = [
    { id: "Hepsi", name: t("news.categories.allNews"), code: "Hepsi" },
    { id: "FinansalRaporlar", name: t("news.categories.financialReports"), code: "FinansalRaporlar" },
    { id: "VergiBilancolari", name: t("news.categories.taxBalanceSheets"), code: "VergiBilancolari" },
    { id: "TemettuveSermayeArttirimi", name: t("news.categories.dividendAndCapitalIncrease"), code: "TemettuveSermayeArttirimi" },
    { id: "OzelDurumAciklamalari", name: t("news.categories.specialCaseDisclosures"), code: "OzelDurumAciklamalari" },
    { id: "Diğer", name: t("news.categories.other"), code: "Diger" }
  ];
  const newsCategories = hideOtherCategory
    ? allNewsCategories.filter((c) => c.id !== "Diğer")
    : allNewsCategories;
  const [activeCategory, setActiveCategory] = useState(newsCategories[0]);

  const normalizeConstituentId = (value) => {
    if (!value) return "ANY";
    const upper = value.toUpperCase();
    if (upper === "XGMYO") {
      return "XGYMO";
    }
    return upper;
  };

  const fetchNews = async (reset = false) => {
    // Prevent concurrent requests and respect hasMore flag
    if (loadingRef.current || (!reset && !hasMore)) {
      console.log(`[NewsComponent] Skip fetch: loading=${loadingRef.current}, hasMore=${hasMore}, reset=${reset}`);
      return;
    }

    // Check if we should fetch based on hasEntityId condition
    if (hasEntityId && (!entityIds || entityIds.length === 0)) {
      console.log('[NewsComponent] Skip fetch: hasEntityId=true but entityIds is empty');
      reset ? setInitialLoading(false) : setListLoading(false);
      return;
    }

    // Set loading state and capture current request ID
    loadingRef.current = true;
    reset ? setInitialLoading(true) : setListLoading(true);
    const requestId = ++requestIdRef.current;

    // Calculate offset - reset to 0 if requested
    const currentOffset = reset ? 0 : offsetRef.current;

    // Prepare API parameters
    const securityCodes = entityIds ?? [];
    // Calculate date based on beginOfDate prop (number of years)
    const dateValue = moment().add(-beginOfDate, "years").format("YYYY-MM-DD");
    const constituentId = normalizeConstituentId(constutientEntityId);
    const newsTypes = [activeCategory.code];

    console.log(`[NewsComponent] Fetching news: offset=${currentOffset}, limit=${ITEMS_PER_PAGE}, constituentId=${constituentId}, years=${beginOfDate}, reset=${reset}`);

    try {
      console.log(`[NewsComponent] API call with params: securityCodes=${JSON.stringify(securityCodes)}, offset=${currentOffset}, limit=${ITEMS_PER_PAGE}`);
      const res = await api.apiKapNewsGet(
        constituentId,
        newsTypes,
        dateValue,
        token,
        securityCodes,
        ITEMS_PER_PAGE,
        currentOffset
      );

      // Only process if this is still the most recent request
      if (requestId !== requestIdRef.current) {
        console.log("[NewsComponent] Discarding stale response");
        return;
      }

      console.log(`[NewsComponent] KapApi response ⇒ ${res.data.data?.length || 0} items`);
      const rows = res.data.data ?? [];
      console.log("[NewsComponent] Received data:", rows.length > 0 ? `${rows.length} items` : "No items");

      // Process response immediately to minimize delay
      if (rows.length === 0) {
        // No data returned - we've reached the end
        console.log("[NewsComponent] No more news to load");
        setHasMore(false);
      } else {
        // Update data and pagination state
        setNewsTableData((prev) => reset ? rows : [...prev, ...rows]);
        offsetRef.current = currentOffset + rows.length;
        
        // Sadece DÖNEN veri, TALEP EDİLEN'den azsa, veri bitmiş demektir
        // Fakat apiden bazen beklenen limitten az dönebilir, bu yüzden hasMore
        // değerini false yapmak yerine, gelen data var mı kontrolüne bağlıyoruz.
        console.log(`[NewsComponent] Received ${rows.length} items`);
        setHasMore(true); // Gelen data 0 olmadığı sürece hep true setliyoruz. Veri biterse bir sonraki turda satır 133 e düşüp false olacak.
      }

      // Reset loading state with minimal delay
      setTimeout(() => {
        loadingRef.current = false;
        reset ? setInitialLoading(false) : setListLoading(false);
      }, 300);
    } catch (err) {
      console.error("KapApi error ⇒", err);

      // Only update state if this is still the most recent request
      if (requestId === requestIdRef.current) {
        loadingRef.current = false;
        reset ? setInitialLoading(false) : setListLoading(false);
      }
    }
  };

  // Function to handle initial data load
  const loadInitialData = () => {
    console.log("[NewsComponent] Loading initial data...");

    // Reset pagination state
    offsetRef.current = 0;
    requestIdRef.current = 0;
    setHasMore(true);
    loadingRef.current = false;

    // Clear previous data
    setNewsTableData([]);

    // Only fetch if hasEntityId is false or if entityIds has values
    if (!hasEntityId || (entityIds && entityIds.length > 0)) {
      fetchNews(true);
    } else {
      // No data to load but we need to stop loading state
      setInitialLoading(false);
    }
  };

  // Effect for data changes
  useEffect(() => {
    loadInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCategory, entityIds, constutientEntityId, token, mainNews, hasEntityId, beginOfDate]);

  return (
    <div>
      {showLiveTicker !== false && (
        <Row>
          <LiveTickerBar />
        </Row>
      )}

      <Container className="m-t-0">
        <Row className="m-t-5">
          <Col className="p-0">
            <ParallelButtonSelection
              titles={newsCategories}
              activeTitle={activeCategory.id}
              clickTitle={(cat) => setActiveCategory(cat)}
            />
          </Col>
        </Row>
      </Container>


      <Card className="grid-card" style={{ width: "100%" }}>
        {initialLoading && <Spinner height="735px" />}

        {!initialLoading && (
          <>
            <NewsTable
              columns={columns}
              paginator={false}
              rowLength={7}
              tableData={newsTableData}
              visibleSearch={visibleSearch}
              scrollHeight="735px"
              onScrollBottom={() => {
                console.log(`[NewsComponent] onScrollBottom called: listLoading=${listLoading}, hasMore=${hasMore}`);
                if (!listLoading && hasMore) {
                  console.log("[NewsComponent] Scroll reached bottom, loading more news...");
                  fetchNews(false);
                } else {
                  console.log(`[NewsComponent] Skip loading: listLoading=${listLoading}, hasMore=${hasMore}`);
                }
              }}
              emptyMessage={t('news.noNews')}
            />

            {listLoading && (
              <div className="compact-loading-indicator">
                <FontAwesomeIcon icon={faSpinner} spin className="loading-spinner-icon" />
                <span className="loading-message">{t('news.loadingMore')}</span>
              </div>
            )}

            {!listLoading && !hasMore && newsTableData.length > 0 && (
              <div className="end-of-news-message">
                {t('news.noMoreNews')}
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
};

export default News;
